import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { WhatsappLogo, PaperPlaneTilt, Trash, ChatCircle } from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';

export default function WhatsAppEmulator() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    to: '',
    body: '',
    from_number: '+56912345678'
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await api.get('/emulator/whatsapp/messages');
      setMessages(res.data.reverse());
    } catch (error) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/whatsapp/send', form);
      toast.success('Mensaje enviado (emulado)');
      setForm({ ...form, body: '' });
      fetchMessages();
    } catch (error) {
      toast.error('Error enviando mensaje');
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('¿Limpiar todos los mensajes?')) return;
    try {
      await api.delete('/emulator/whatsapp/messages');
      toast.success('Mensajes limpiados');
      fetchMessages();
    } catch (error) {
      toast.error('Error limpiando');
    }
  };

  return (
    <div data-testid="whatsapp-emulator-page">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
            Sandbox · Testing
          </p>
          <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">
            Emulador WhatsApp
          </h1>
          <p className="mt-2 text-neutral-600 text-sm">
            Envía mensajes simulados sin necesidad de credenciales de Twilio o Meta.
          </p>
        </div>
        <button
          onClick={handleClear}
          data-testid="clear-messages-btn"
          className="flex items-center px-4 py-2.5 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 text-sm font-medium"
        >
          <Trash size={16} className="mr-2" />
          Limpiar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Send Form */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-md p-6 sticky top-6">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-md bg-green-50 border border-green-200 flex items-center justify-center">
                <WhatsappLogo size={20} weight="fill" className="text-green-600" />
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-bold text-neutral-900">Enviar Mensaje</h2>
                <p className="text-xs text-neutral-500">Simulación local</p>
              </div>
            </div>

            <form onSubmit={handleSend} className="space-y-4" data-testid="send-message-form">
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  Número destino
                </label>
                <input
                  type="text"
                  value={form.to}
                  onChange={(e) => setForm({ ...form, to: e.target.value })}
                  data-testid="whatsapp-to-input"
                  required
                  placeholder="+56912345678"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  Mensaje
                </label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  data-testid="whatsapp-body-input"
                  required
                  rows={5}
                  placeholder="Hola conductor, tienes un transporte asignado..."
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="send-whatsapp-btn"
                className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60 text-sm font-medium uppercase tracking-widest"
              >
                <PaperPlaneTilt size={16} className="mr-2" weight="bold" />
                {sending ? 'Enviando...' : 'Enviar'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-neutral-200">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                Templates rápidos
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => setForm({ ...form, body: 'Hola conductor, tienes un transporte asignado. Ingresa aquí: https://sroute.com/t/ABC123' })}
                  data-testid="template-asignacion-btn"
                  className="w-full text-left px-3 py-2 text-xs border border-neutral-200 rounded hover:bg-neutral-50"
                >
                  Asignación de transporte
                </button>
                <button
                  onClick={() => setForm({ ...form, body: 'Preséntate al andén 3 a las 14:00 hrs para carga.' })}
                  data-testid="template-llamado-btn"
                  className="w-full text-left px-3 py-2 text-xs border border-neutral-200 rounded hover:bg-neutral-50"
                >
                  Llamado a carga
                </button>
                <button
                  onClick={() => setForm({ ...form, body: 'Recordatorio: transporte pendiente de finalización.' })}
                  data-testid="template-recordatorio-btn"
                  className="w-full text-left px-3 py-2 text-xs border border-neutral-200 rounded hover:bg-neutral-50"
                >
                  Recordatorio
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Feed */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div className="flex items-center">
                <ChatCircle size={20} className="mr-2 text-neutral-500" />
                <h2 className="text-lg font-bold text-neutral-900">Mensajes Enviados</h2>
              </div>
              <span className="text-xs uppercase tracking-widest text-neutral-500">
                {messages.length} mensajes
              </span>
            </div>

            <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
              {loading ? (
                <div className="p-12 text-center text-neutral-500">Cargando...</div>
              ) : messages.length === 0 ? (
                <div className="p-12 text-center">
                  <WhatsappLogo size={48} className="mx-auto text-neutral-300 mb-3" />
                  <p className="text-neutral-500">No hay mensajes enviados</p>
                </div>
              ) : (
                <div className="divide-y divide-neutral-100" data-testid="messages-list">
                  {messages.map(msg => (
                    <div key={msg.id} className="p-6 hover:bg-neutral-50" data-testid={`message-${msg.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                            <WhatsappLogo size={16} weight="fill" className="text-green-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-xs uppercase tracking-widest text-neutral-500">Para</p>
                            <p className="font-mono text-sm font-medium text-neutral-900">{msg.to}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 uppercase tracking-widest">
                            {msg.status}
                          </span>
                          <p className="text-xs text-neutral-500 mt-1">
                            {new Date(msg.created_at).toLocaleString('es-CL')}
                          </p>
                        </div>
                      </div>
                      <div className="ml-11 mt-3 p-3 bg-neutral-50 rounded-md border-l-2 border-green-500">
                        <p className="text-sm text-neutral-800 whitespace-pre-wrap">{msg.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
